/**
 * Desglose visual de los 5 factores del EBR (Cliente, Producto, Geografía, Transacciones, Canal).
 * Fuente única: GET /api/riesgo/perfil-ebr/{doc_id}?tipo=cliente|prospecto.
 *
 * Responsabilidad: mostrar el grado CONSOLIDADO de los 5 factores + presencia de dato por
 * subvariable ("Sin dato" cuando la variable base no está capturada, para distinguir "bajo por
 * dato" de "bajo por falta de dato"). NO recalcula ni persiste — solo lee y visualiza.
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { NIVEL_TONE, NIVEL_LBL, SIN_DATO_TONE, GRADO_SEMAFORO, MOTIVO_LABEL } from "@/lib/riesgoTokens";

function NivelBadge({ nivel, presente, testid }) {
  if (!presente) {
    return (
      <span data-testid={testid}
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${SIN_DATO_TONE}`}
        title="Variable no capturada en el expediente — el motor asume 'bajo' por defecto, pero puede no reflejar el riesgo real.">
        Sin dato
      </span>
    );
  }
  const tone = NIVEL_TONE[nivel] || SIN_DATO_TONE;
  return (
    <span data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${tone}`}>
      {NIVEL_LBL[nivel] || nivel || "—"}
    </span>
  );
}

export function RiesgoEBRBreakdown({ docId, tipo = "cliente", onRecalculado, canRecalcular = true, dense = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalBusy, setRecalBusy] = useState(false);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/riesgo/perfil-ebr/${docId}`, { params: { tipo } });
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [docId, tipo]);

  useEffect(() => { load(); }, [load]);

  const recalcular = async () => {
    if (tipo !== "cliente") {
      // Los prospectos NO tienen recálculo puntual expuesto (se recalculan al alta / conversión).
      toast.info("El grado del prospecto se recalcula automáticamente al guardar cambios y al convertirlo en cliente.");
      return;
    }
    setRecalBusy(true);
    try {
      const { data: r } = await api.post("/riesgo/recalcular-cliente", { cliente_id: docId });
      toast.success(`Recálculo aplicado: grado ${r.grado} (${r.puntaje_total} pts) · metodología v${r.metodologia_version}`);
      await load();
      onRecalculado?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRecalBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div data-testid="ebr-breakdown-loading" className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando desglose EBR…
      </div>
    );
  }

  const vigente = data.vigente || {};
  const persistido = data.persistido || {};
  const respuestas = vigente.respuestas || {};
  const presencia = data.presencia || {};
  const factores = data.factores || [];
  const grado = vigente.grado || "—";
  const sem = GRADO_SEMAFORO[grado] || GRADO_SEMAFORO.Bajo;
  const factoresQueElevan = vigente.factores_que_elevan || [];

  // "Sin dato" agregado a nivel de factor: si TODAS las subvariables del factor están sin dato
  // (y no es el factor cliente, que siempre tiene condicion=true), lo marcamos como tal.
  const factorSinDatos = (f) => {
    const pres = presencia[f.clave] || {};
    return f.subvariables.every((s) => pres[s.clave] === false);
  };

  return (
    <div data-testid="ebr-breakdown" className="space-y-3">
      {/* Semáforo consolidado (los 5 factores) */}
      <div data-testid={`ebr-semaforo-${vigente.semaforo || "verde"}`}
        className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${sem.cls}`}>
        <sem.Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold" data-testid="ebr-grado-vigente">
            Grado EBR consolidado: {grado}
            {vigente.puntaje_total !== undefined && (
              <span className="ml-1 text-xs font-normal">· {vigente.puntaje_total} pts</span>
            )}
          </p>
          <p className="text-[11px] mt-0.5">
            {MOTIVO_LABEL[vigente.riesgo_motivo] || "Cálculo consolidado de los 5 factores del EBR."}
            {vigente.metodologia_version && (
              <span className="opacity-80"> · metodología v{vigente.metodologia_version}</span>
            )}
            {vigente.umbrales && (
              <span className="opacity-80"> · umbrales medio≥{vigente.umbrales.medio} / alto≥{vigente.umbrales.alto}</span>
            )}
          </p>
          {factoresQueElevan.length > 0 && (
            <p className="mt-1 text-[11px]" data-testid="ebr-elevan">
              <span className="font-medium">Factores que elevan:</span> {factoresQueElevan.join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* Aviso si el persistido está desactualizado respecto al vigente */}
      {data.desactualizado && (
        <div data-testid="ebr-desactualizado"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            El grado guardado en el expediente ({persistido.grado || "—"}, metodología v{persistido.metodologia_version || "—"}){" "}
            no coincide con el cálculo vigente ({grado}, v{vigente.metodologia_version}). Recalcula para actualizar el expediente.
            {canRecalcular && tipo === "cliente" && (
              <button data-testid="ebr-recalcular-btn" onClick={recalcular} disabled={recalBusy}
                className="ml-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                {recalBusy
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <RefreshCw className="h-3 w-3" />} Recalcular
              </button>
            )}
          </div>
        </div>
      )}

      {/* Desglose de los 5 factores */}
      <div className={dense ? "grid gap-2 sm:grid-cols-2" : "grid gap-3 sm:grid-cols-2"}>
        {factores.map((f) => {
          const subvars = f.subvariables || [];
          const respFactor = respuestas[f.clave] || {};
          const presFactor = presencia[f.clave] || {};
          const puntFactor = vigente.puntajes_factor?.[f.clave];
          const allEmpty = factorSinDatos(f);
          return (
            <div key={f.clave} data-testid={`ebr-factor-${f.clave}`}
              className="rounded-md border border-slate-200 bg-white p-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {f.nombre} <span className="text-slate-400">({f.ponderacion}%)</span>
                </p>
                {puntFactor && (
                  <span className="text-[10px] text-slate-400">score {puntFactor.score}</span>
                )}
              </div>
              {allEmpty ? (
                <p className="mt-1.5 text-[11px] italic text-slate-400" data-testid={`ebr-factor-${f.clave}-sin-datos`}>
                  Sin datos capturados — el motor asume bajo por defecto.
                </p>
              ) : (
                <ul className="mt-1.5 space-y-1">
                  {subvars.map((s) => (
                    <li key={s.clave} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-600">{s.nombre}</span>
                      <NivelBadge
                        nivel={respFactor[s.clave]}
                        presente={presFactor[s.clave] !== false}
                        testid={`ebr-nivel-${f.clave}-${s.clave}`}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Botón recalcular explícito (cuando NO hay desactualización — para forzar) */}
      {!data.desactualizado && canRecalcular && tipo === "cliente" && (
        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          <span>
            Cálculo alineado con la metodología vigente (v{vigente.metodologia_version}). Última evaluación registrada:{" "}
            {persistido.updated_at ? String(persistido.updated_at).slice(0, 10) : "—"}.
          </span>
          <button data-testid="ebr-recalcular-btn" onClick={recalcular} disabled={recalBusy}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            {recalBusy
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />} Recalcular grado
          </button>
        </div>
      )}
    </div>
  );
}

export default RiesgoEBRBreakdown;
